import React, { useState } from "react"
import parse, { DOMNode, domToReact } from "html-react-parser"
import { Divider, FlexContainer, H1, H2, H3, H4, Text, TextLink, Image, Container } from "@carrotfertility/carotene"

export function useMaxMessageLength({
  maxLength
}): { validateMaxLength: (value: string | null) => void; lengthWarningMessage: string; maxLength: number } {
  const [lengthWarningMessage, setLengthWarningMessage] = useState(null)
  function validateMaxLength(value: string): void {
    setLengthWarningMessage(value?.length >= maxLength * 0.75 ? `${value?.length} / ${maxLength}` : null)
  }
  return { validateMaxLength, lengthWarningMessage, maxLength }
}

export function getParsedHtmlElements(htmlString: string): string | JSX.Element | JSX.Element[] {
  const options = {
    /*
       there appears to be a typing issue coming from html-react-parser
       ignoring them for now until we can investigate further
     */
    // @ts-ignore
    replace: ({ attribs, children, name }: DOMNode) => {
      if (!attribs) {
        return
      }
      // @ts-ignore
      switch (name) {
        case "h1":
          return <H1>{domToReact(children, options)}</H1>
        case "h2":
          return <H2>{domToReact(children, options)}</H2>
        case "h3":
          return <H3>{domToReact(children, options)}</H3>
        case "h4":
          return <H4>{domToReact(children, options)}</H4>
        case "ol":
          return (
            <ol style={{ paddingLeft: "1 em", listStyle: "decimal", marginBottom: "16px" }}>
              <FlexContainer direction="column" gap="tiny" paddingLeftRight="small">
                {domToReact(children, options)}
              </FlexContainer>
            </ol>
          )
        case "ul":
          return (
            <ul
              style={{
                marginBottom: "16px",
                paddingLeft: "1em"
              }}
            >
              {domToReact(children, options)}
            </ul>
          )
        case "li":
          return (
            <li style={{ display: "list-item" }}>
              <Text as="span" color="black-80">
                {domToReact(children, options)}
              </Text>
            </li>
          )
        case "img":
          return (
            <Image
              sx={{ marginTop: "small", marginBottom: "small", display: "block" }}
              src={attribs.src}
              altText={attribs.altText ? attribs.altText : "no alt text available"}
            />
          )
        case "hr":
          return <Divider paddingTopBottom="small" color="black10" />
        case "p":
          return (
            <Text as="p" color="black80" style={{ wordBreak: "break-word" }}>
              {domToReact(children, options)}
            </Text>
          )
        case "blockquote":
          return (
            <blockquote style={{ borderLeft: "2px solid #0000001A", margin: "1.5em 10px", padding: "0.5em 10px" }}>
              <div>{domToReact(children, options)}</div>
            </blockquote>
          )
        case "code":
          return (
            <Container
              sx={{ display: "inline-block" }}
              width="auto"
              padding="small"
              background="lightGrey"
              borderRadius="4px"
            >
              <code style={{ fontFamily: "monospace" }}>{domToReact(children, options)}</code>
            </Container>
          )
        case "a":
          return (
            <TextLink
              size="medium"
              target={attribs.target ? attribs.target : "_blank"}
              title={attribs.title}
              to={attribs.href}
              style={{
                display: "inline"
              }}
            >
              {domToReact(children, options)}
            </TextLink>
          )
        case "b":
        case "strong":
          return <Text weight="bold">{domToReact(children, options)}</Text>
        case "i":
        case "em":
          return <Text fontStyle="italic">{domToReact(children, options)}</Text>
        case "u":
          return <Text style={{ textDecoration: "underline" }}>{domToReact(children, options)}</Text>
      }
    }
  }
  return parse(htmlString, options)
}
